<template>
  <div>
    <!-- Upload Button -->
    <div class="mb-3" v-if="!readonly">
      <v-btn
        small
        color="primary"
        @click="openFileDialog"
        :disabled="disabled"
      >
        <v-icon left>mdi-plus</v-icon>
        {{ uploadButtonText }}
      </v-btn>
      <input
        ref="fileInput"
        type="file"
        :accept="fileTypes"
        :multiple="multiple"
        @change="handleFileSelect"
        style="display: none"
      />
    </div>

    <!-- Files Table -->
    <v-data-table
      :headers="headers"
      :items="files"
      :hide-default-footer="true"
      :items-per-page="1000"
      class="custom-table"
      :loading="loading"
    >
      <template #[`item.index`]="{ index }">
        <td class="pr-0 pl-2">{{ index + 1 }}</td>
      </template>

      <template #[`item.name`]="{ item }">
        <td>
          <div class="d-flex align-center">
            <span>{{ item.name }}</span>
          </div>
        </td>
      </template>

      <template #[`item.size`]="{ item }">
        <td>{{ formatFileSize(item.size) }}</td>
      </template>

      <template #[`item.type`]="{ item }">
        <td>{{ getFileType(item.name) }}</td>
      </template>

      <template #[`item.uploadDate`]="{ item }">
        <td>{{ item.uploadDate || formatDate(new Date()) }}</td>
      </template>

      <template #[`item.actions`]="{ item, index }">
        <td>
          <div class="d-flex align-center">
            <v-btn
              class="table-action mr-1"
              icon
              dense
              @click="downloadFile(item)"
            >
              <v-icon>mdi-file-move-outline</v-icon>
            </v-btn>
            <v-btn
              v-if="!readonly && canDeleteFile(item)"
              class="table-action mx-1"
              icon
              dense
              @click="removeFile(index)"
            >
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ElFileUploadTable',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    canDeleteFile: {
      type: Function,
      default: () => true // Default: can delete all files
    },
    uploadButtonText: {
      type: String,
      default: 'Įkelti dokumentą'
    },
    emptyStateText: {
      type: String,
      default: 'Nėra įkeltų failų'
    },
    showUploadDate: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {}
  },
  computed: {
    files() {
      return this.value || []
    },
    fileTypes() {
      return sessionStorage.getItem('file_types') || '.pdf,.jpg,.jpeg,.png'
    },
    maxFileSize() {
      return parseInt(sessionStorage.getItem('max_file_size')) || 10485760 // 10MB default
    },
    headers() {
      const headers = [
        {
          text: '#',
          align: 'start',
          value: 'index',
          sortable: false,
          width: '50px'
        },
        {
          text: this.$t('Veiksmai'),
          align: 'center',
          value: 'actions',
          sortable: false,
          width: '150px'
        },
        {
          text: this.$t('Failo pavadinimas'),
          align: 'start',
          value: 'name',
        },
        {
          text: this.$t('Dydis'),
          align: 'start',
          value: 'size',
          width: '100px'
        },
      ]

      if (this.showUploadDate) {
        headers.push({
          text: this.$t('Įkėlimo data'),
          align: 'start',
          value: 'uploadDate',
          width: '150px'
        })
      }

      return headers
    }
  },
  methods: {
    openFileDialog() {
      this.$refs.fileInput.click()
    },

    handleFileSelect(event) {
      const selectedFiles = Array.from(event.target.files)

      if (selectedFiles.length === 0) return

      // Validate file types
      const invalidFiles = selectedFiles.filter(file => !this.isValidFileType(file))
      if (invalidFiles.length > 0) {
        let allowedTypes = this.fileTypes
        if (Array.isArray(this.fileTypes)) {
          allowedTypes = this.fileTypes.join(', ')
        } else if (typeof this.fileTypes === 'string') {
          // Check if it's a JSON array string
          if (this.fileTypes.startsWith('[') && this.fileTypes.endsWith(']')) {
            try {
              const parsedArray = JSON.parse(this.fileTypes)
              allowedTypes = parsedArray.join(', ')
            } catch (e) {
              allowedTypes = this.fileTypes
            }
          } else {
            allowedTypes = this.fileTypes
          }
        } else {
          allowedTypes = '.pdf,.jpg,.jpeg,.png'
        }
        this.$root.$emit('DATA_ERROR_DIRECT_TEXT', `Nepalaikomi failų tipai. Leidžiami tipai: ${allowedTypes}. Netinkami failai: ${invalidFiles.map(f => f.name).join(', ')}`)
        return
      }

      console.log(this.maxFileSize)
      // Validate file sizes
      const oversizedFiles = selectedFiles.filter(file => file.size > this.maxFileSize)
      if (oversizedFiles.length > 0) {
        this.$root.$emit('DATA_ERROR_DIRECT_TEXT', `Failai viršija maksimalų dydį (${this.formatFileSize(this.maxFileSize)}): ${oversizedFiles.map(f => f.name).join(', ')}`)
        return
      }

      const newFiles = selectedFiles.map(file => ({
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file),
        uploadDate: this.formatDate(new Date()),
        id: null
      }))

      this.$emit('input', [...this.files, ...newFiles])
      this.$emit('files-added', newFiles)

      // Reset input
      event.target.value = ''
    },

    removeFile(index) {
      const fileToRemove = this.files[index]

      // Clean up object URL to prevent memory leaks
      if (fileToRemove.url) {
        URL.revokeObjectURL(fileToRemove.url)
      }

      const updatedFiles = [...this.files]
      updatedFiles.splice(index, 1)

      this.$emit('input', updatedFiles)
      this.$emit('file-removed', fileToRemove)
    },

    openFile(file) {
      var fileLink = document.createElement('a')
      fileLink.href = file.file
      fileLink.setAttribute(
        'download',
        this.safeFileName(file.original_filename)
      )
      document.body.appendChild(fileLink)
      fileLink.click()
    },


    async downloadFile(item) {
      try {
        if (item.id) {
          // Download from backend using axios
          this.$axios
            .get(`uploads/${item.id}/download`)
            .then((res) => {
              this.openFile(res.data)
            })
            .catch((err) => {
              const errData = JSON.parse(
                Buffer.from(err.response.data).toString('utf8')
              )
              this.$root.$emit('DATA_ERROR', errData.message)
            })
            .finally(() => {})

        } else if (item.file) {
          // Use the same binary file handling logic as ModalPersonDocument
          // Pass the item name as well for proper filename
          this.handleBinaryFile(item.file, item.name)

        } else {
          this.$root.$emit('DATA_ERROR', 'No file available for download')
        }

        this.$emit('file-downloaded', item)

      } catch (error) {
        console.error('Download error:', error)
        if (error.response && error.response.data) {
          try {
            const errorData = JSON.parse(
              Buffer.from(error.response.data).toString('utf8')
            )
            this.$root.$emit('DATA_ERROR', errorData.message || 'Download failed')
          } catch (parseError) {
            this.$root.$emit('DATA_ERROR', 'Download failed')
          }
        } else {
          this.$root.$emit('DATA_ERROR', 'Download failed: ' + error.message)
        }
      }
    },

    handleBinaryFile(fileData, itemName = null) {
      try {
        // Handle binary file data - could be base64 encoded or raw binary
        let blob
        let filename = itemName || 'document'

        if (fileData.original_filename) {
          filename = fileData.original_filename
        } else if (fileData.name && !itemName) {
          filename = fileData.name
        }

        if (fileData.file) {
          if (fileData.file instanceof File) {
            // If fileData.file is a File object, treat it as such
            blob = fileData.file
            filename = itemName || fileData.file.name
          } else if (typeof fileData.file === 'string') {
            // If it's already a file URL or blob URL
            const link = document.createElement('a')
            link.href = fileData.file
            link.download = this.safeFileName(fileData.original_filename || filename)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            return
          }
        }

        // Only process other file types if we don't already have a blob
        if (!blob) {
          if (fileData.data) {
            // If it's base64 encoded data
            const byteCharacters = atob(fileData.data)
            const byteNumbers = new Array(byteCharacters.length)
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)
            blob = new Blob([byteArray], { type: fileData.mime_type || 'application/octet-stream' })
          } else if (fileData instanceof File) {
            // If it's already a File object
            blob = fileData
            // Use the item name if provided, otherwise use the file's name
            filename = itemName || fileData.name
          } else {
            // If it's raw binary data
            blob = new Blob([fileData], { type: 'application/octet-stream' })
          }
        }

        // Create download link
        const url = window.URL.createObjectURL(blob)
        const fileLink = document.createElement('a')
        fileLink.href = url
        const safeFilename = this.safeFileName(filename)
        fileLink.setAttribute('download', safeFilename)
        document.body.appendChild(fileLink)
        fileLink.click()
        document.body.removeChild(fileLink)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$root.$emit('DATA_ERROR', 'Error processing file: ' + error.message)
      }
    },

    safeFileName(filename) {
      // Simple filename sanitization - remove or replace invalid characters
      return filename.replace(/[^a-zA-Z0-9.-]/g, '_')
    },

    getFileType(filename) {
      if (!filename) return ''
      const extension = filename.split('.').pop().toLowerCase()
      return extension.toUpperCase()
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatDate(date) {
      return moment(date).format('YYYY-MM-DD HH:mm')
    },

    isImage(filename) {
      if (!filename) return false
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
      const extension = filename.split('.').pop().toLowerCase()
      return imageExtensions.includes(extension)
    },

    isPdf(filename) {
      if (!filename) return false
      return filename.toLowerCase().endsWith('.pdf')
    },

    isValidFileType(file) {
      if (!this.fileTypes) return true // If no file types, allow all files

      // Handle both array and string formats
      let acceptedTypes = []
      if (Array.isArray(this.fileTypes)) {
        acceptedTypes = this.fileTypes.map(type => type.trim().toLowerCase())
      } else if (typeof this.fileTypes === 'string') {
        // Check if it's a JSON array string
        if (this.fileTypes.startsWith('[') && this.fileTypes.endsWith(']')) {
          try {
            acceptedTypes = JSON.parse(this.fileTypes).map(type => type.trim().toLowerCase())
          } catch (e) {
            // If JSON parsing fails, fall back to comma splitting
            acceptedTypes = this.fileTypes.split(',').map(type => type.trim().toLowerCase())
          }
        } else {
          // Regular comma-separated string
          acceptedTypes = this.fileTypes.split(',').map(type => type.trim().toLowerCase())
        }
      }


      const fileName = file.name.toLowerCase()
      const fileExtension = fileName.split('.').pop()

      // Check if file extension matches any accepted type
      return acceptedTypes.some(type => {
        if (type.includes('/')) {
          // MIME type validation (e.g., image/jpeg, application/pdf)
          return file.type === type
        } else {
          // Extension-based validation (e.g., pdf, jpg, docx)
          return fileExtension === type
        }
      })
    },
  },


  beforeDestroy() {
    // Clean up all object URLs to prevent memory leaks
    this.files.forEach(file => {
      if (file.url) {
        URL.revokeObjectURL(file.url)
      }
    })
  }
}
</script>

<style scoped>
.custom-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.custom-table >>> .v-data-table__wrapper {
  border-radius: 4px;
}

.table-action {
  line-height: 24px;
}
.table-action-red {
  color: red;
}
.table-action:hover {
  color: #1976d2;
}
</style>
